import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AppConfig {
  production: boolean;
  apiUrl: string;
  apiVersion: string;
  appName: string;
  enableDebugInfo: boolean;
  featureFlags: {
    [key: string]: boolean;
  };
  caching: {
    enableServiceWorker: boolean;
    cacheTimeoutMs: number;
    maxCacheSize: number;
  };
  analytics: {
    enabled: boolean;
    trackingId: string;
  };
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableConsoleLogging: boolean;
    enableRemoteLogging: boolean;
  };
}

export interface UserPreferences {
  language: string;
  theme: 'light' | 'dark' | 'auto';
  dateFormat: string;
  timeFormat: '12h' | '24h';
  currency: string;
  timezone: string;
  itemsPerPage: number;
  enableNotifications: boolean;
  enableSounds: boolean;
  accessibilityMode: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private readonly PREFERENCES_KEY = 'insee-user-preferences';
  
  private configSubject = new BehaviorSubject<AppConfig>(environment as AppConfig);
  private userPreferencesSubject = new BehaviorSubject<UserPreferences>(this.getDefaultPreferences());

  config$: Observable<AppConfig> = this.configSubject.asObservable();
  userPreferences$: Observable<UserPreferences> = this.userPreferencesSubject.asObservable();

  constructor() {
    this.loadUserPreferences();
  }

  /**
   * Get current application configuration
   */
  getConfig(): AppConfig {
    return this.configSubject.value;
  }

  /**
   * Get current user preferences
   */
  getUserPreferences(): UserPreferences {
    return this.userPreferencesSubject.value;
  }

  /**
   * Update user preferences
   */
  updateUserPreferences(preferences: Partial<UserPreferences>): void {
    const current = this.userPreferencesSubject.value;
    const updated = { ...current, ...preferences };
    this.userPreferencesSubject.next(updated);
    this.saveUserPreferences(updated);
  }

  /**
   * Reset user preferences to defaults
   */
  resetUserPreferences(): void {
    const defaults = this.getDefaultPreferences();
    this.userPreferencesSubject.next(defaults);
    this.saveUserPreferences(defaults);
  }

  /**
   * Get API endpoint URL
   */
  getApiUrl(endpoint: string = ''): string {
    const config = this.configSubject.value;
    const baseUrl = `${config.apiUrl}/api/${config.apiVersion}`;
    return endpoint ? `${baseUrl}/${endpoint.replace(/^\//, '')}` : baseUrl;
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature: string): boolean {
    const config = this.configSubject.value;
    return config.featureFlags[feature] || false;
  }

  /**
   * Get application name with version
   */
  getAppDisplayName(): string {
    const config = this.configSubject.value;
    return config.appName;
  }

  /**
   * Check if debug mode is enabled
   */
  isDebugMode(): boolean {
    const config = this.configSubject.value;
    return config.enableDebugInfo && !config.production;
  }

  /**
   * Get logging configuration
   */
  getLoggingConfig() {
    return this.configSubject.value.logging;
  }

  /**
   * Get caching configuration
   */
  getCachingConfig() {
    return this.configSubject.value.caching;
  }

  /**
   * Get analytics configuration
   */
  getAnalyticsConfig() {
    return this.configSubject.value.analytics;
  }

  private getDefaultPreferences(): UserPreferences {
    return {
      language: 'fr', // French as default for INSEE
      theme: 'auto',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      currency: 'EUR',
      timezone: 'Europe/Paris',
      itemsPerPage: 25,
      enableNotifications: true,
      enableSounds: true,
      accessibilityMode: false
    };
  }

  private loadUserPreferences(): void {
    try {
      const stored = localStorage.getItem(this.PREFERENCES_KEY);
      if (stored) {
        const preferences: UserPreferences = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        const merged = { ...this.getDefaultPreferences(), ...preferences };
        this.userPreferencesSubject.next(merged);
      }
    } catch (error) {
      console.warn('Failed to load user preferences:', error);
    }
  }

  private saveUserPreferences(preferences: UserPreferences): void {
    try {
      localStorage.setItem(this.PREFERENCES_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save user preferences:', error);
    }
  }
}